import { getToken } from '../../Utils/authUtils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { DASHBOARD } from '../../constant';

/**
 * Billing: the invoice register, payments and cancellations.
 *
 * Grouped in one module the way rtk-apis/reports/reportLabels is — these four calls
 * are always used together by the billing panel.
 */

export type InvoiceStatus = 'DRAFT' | 'SENT' | 'PART_PAID' | 'PAID' | 'VOID';

export interface InvoicePayment {
  _id?: string;
  amount: number;
  paidOn: string;
  method?: string;
  reference?: string;
  note?: string;
}

export interface InvoiceRecord {
  _id: string;
  invoiceNumber: string;
  clinicId: string;
  clinicName: string;
  periodLabel: string;
  periodStart: string;
  periodEnd: string;
  basis: 'per_study' | 'per_report' | 'monthly';
  rate: number;
  currency: string;
  total: number;
  amountPaid: number;
  balance: number;
  status: InvoiceStatus;
  issuedOn: string;
  dueOn: string;
  sentAt?: string;
  daysOverdue: number;
  payments: InvoicePayment[];
  source: 'manual' | 'scheduled';
  voided: boolean;
}

export interface ArSummary {
  outstanding: number;
  overdueTotal: number;
  overdueCount: number;
  openCount: number;
  buckets: { current: number; d1_30: number; d31_60: number; d61_90: number; d90plus: number };
}

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: 'Bearer ' + getToken(),
});

/** Read the response body once, then decide — reading twice throws on a used stream. */
const handle = async (res: Response, rejectWithValue: (v: unknown) => unknown) => {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    return rejectWithValue(body?.msg || 'Request failed');
  }
  return body;
};

export const getInvoices = createAsyncThunk(
  'dashboard/getInvoices',
  async (
    arg: {
      clinicId?: string;
      status?: InvoiceStatus;
      outstanding?: boolean;
      year?: number;
      from?: string;
      to?: string;
    } | void,
    { rejectWithValue }
  ) => {
    try {
      const p = new URLSearchParams();
      if (arg?.clinicId) {
        p.append('clinicId', arg.clinicId);
      }
      if (arg?.status) {
        p.append('status', arg.status);
      }
      if (arg?.outstanding) {
        p.append('outstanding', 'true');
      }
      if (arg?.year) {
        p.append('year', String(arg.year));
      }
      if (arg?.from) {
        p.append('from', arg.from);
      }
      if (arg?.to) {
        p.append('to', arg.to);
      }
      const qs = p.toString();
      const res = await fetch(qs ? `${DASHBOARD.INVOICES}?${qs}` : DASHBOARD.INVOICES, {
        method: 'GET',
        headers: authHeaders(),
      });
      return await handle(res, rejectWithValue);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const recordPayment = createAsyncThunk(
  'dashboard/recordPayment',
  async (
    arg: {
      invoiceId: string;
      amount: number;
      paidOn?: string;
      method?: string;
      reference?: string;
      note?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await fetch(DASHBOARD.INVOICE_PAYMENT, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(arg),
      });
      return await handle(res, rejectWithValue);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const voidInvoice = createAsyncThunk(
  'dashboard/voidInvoice',
  async (arg: { invoiceId: string; reason?: string }, { rejectWithValue }) => {
    try {
      const res = await fetch(DASHBOARD.INVOICE_VOID, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(arg),
      });
      return await handle(res, rejectWithValue);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * Generate invoices on demand for an arbitrary date range, one per clinic — the manual
 * equivalent of the automatic per-clinic schedule. Reuses the same duplicate-period
 * protection, so re-running this for a range that already has invoices is a no-op for
 * those clinics rather than a double-bill.
 */
export const generateInvoices = createAsyncThunk(
  'dashboard/generateInvoices',
  async (
    arg: { from: string; to: string; clinicIds?: string[]; send?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const res = await fetch(DASHBOARD.INVOICE_GENERATE, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(arg),
      });
      return await handle(res, rejectWithValue);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export interface InvoicePreviewLine {
  description: string;
  detail?: string;
  qty: number;
  unitRate: number;
  amount: number;
}

export interface InvoicePreview {
  existing: boolean;
  invoiceNumber?: string;
  status?: string;
  clinicName?: string;
  currency: string;
  subtotal: number;
  tax: number;
  total: number;
  lines: InvoicePreviewLine[];
  periodLabel: string;
}

/** Numbers-only preview for a clinic + range — never persists or sends anything. */
export const previewInvoiceRange = createAsyncThunk(
  'dashboard/previewInvoiceRange',
  async (arg: { clinicId: string; from: string; to: string }, { rejectWithValue }) => {
    try {
      const res = await fetch(DASHBOARD.INVOICE_GENERATE_PREVIEW, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(arg),
      });
      return (await handle(res, rejectWithValue)) as { data: InvoicePreview };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/** Chase an unpaid invoice. Manual only — nothing chases automatically. */
export const sendReminder = createAsyncThunk(
  'dashboard/sendReminder',
  async (arg: { invoiceId: string }, { rejectWithValue }) => {
    try {
      const res = await fetch(DASHBOARD.INVOICE_REMINDER, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(arg),
      });
      return await handle(res, rejectWithValue);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const resendInvoice = createAsyncThunk(
  'dashboard/resendInvoice',
  async (arg: { invoiceId: string }, { rejectWithValue }) => {
    try {
      const res = await fetch(DASHBOARD.INVOICE_RESEND, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(arg),
      });
      return await handle(res, rejectWithValue);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
